import type { AuthUser } from '@/api/auth';
import { isFeatureOn } from '@/lib/appConfig';
import { IS_APP_BUILD } from '@/lib/appBrand';
import { hasLiveSection, hasRoomSection } from '@/lib/callSections';
import { computeHasFeature } from '@/stores/auth';

/**
 * Что из звонков видит календарь ученика — ОДНО правило на событие, кнопку «Войти» и запрос (02.10.2026,
 * решение владельца к сборке 1.1.1: в комнату и на живой урок в приложении входят только из календаря).
 *
 * Есть раздел в `sections` (`lib/callSections.ts`) — событие видно, в карточке кнопка «Войти».
 * Нет раздела — нет ни события, ни кнопки, ни запроса. Замков, «скоро» и нажатий в пустоту нет ни в одном случае.
 *
 * Поверх раздела стоит серверный выключатель: его снимают в админке, и календарь начинает показывать события
 * сам, без новой сборки.
 */

/**
 * Живые уроки: раздел `live` И выключатель `live` — и на сайте, и в приложении.
 *
 * ⚠️ ВЫКЛЮЧАТЕЛЬ ИМЕННО `live`, А НЕ `video`. До 02.10.2026 расписание ученика смотрело на `video`, а `live` —
 * тот, что стоит в админке и назван владельцем, — не читал никто: снять его значило не получить ничего. На проде
 * в день правки оба выключены (`features.live = false`, `features.video = false`), поведение не изменилось.
 * `video` остаётся у расписания куратора.
 *
 * Без раздела запрос `GET /student/schedule` не уходит вовсе: сервер ответил бы 403, и этот отказ остался бы в
 * трафике — ровно то, от чего уходили 19.09.2026 (ревьюер Apple видел отказы в сети).
 */
export function showLive(
  user: AuthUser | null,
  on: boolean = isFeatureOn('live'),
  app: boolean = IS_APP_BUILD,
): boolean {
  return on && hasLiveSection(user, app);
}

/**
 * Разговорные комнаты: выключатель `speaking_peers`; в приложении — ещё и раздел `speaking_peers`.
 *
 * ⚠️ НА САЙТЕ РАЗДЕЛ НЕ ТРЕБУЕТСЯ — как было. Сервер комнату тарифом не закрывает, а адресует её группе или
 * потоку; платник без `speaking_peers` в тарифе видит на сайте комнаты своей группы, и правило раздела их бы
 * отняло. В приложении маршрут комнаты стоит под разделом (`app/router.tsx`), поэтому и событие — под ним:
 * иначе кнопка «Войти» уводила бы на главную.
 */
export function showRooms(
  user: AuthUser | null,
  on: boolean = isFeatureOn('speaking_peers'),
  app: boolean = IS_APP_BUILD,
): boolean {
  if (!on) return false;
  return app ? hasRoomSection(user, true) : true;
}

/**
 * Кнопка «Код посещения»: код вводят на живом уроке, без уроков она ведёт в отказ (`attendance` закрыт разделом
 * на сервере). Нет живых уроков или раздела посещаемости — нет и кнопки.
 */
export function showAttendanceCode(
  user: AuthUser | null,
  on: boolean = isFeatureOn('live'),
  app: boolean = IS_APP_BUILD,
): boolean {
  return showLive(user, on, app) && computeHasFeature(user, 'attendance', app);
}

/**
 * Внутренний адрес события, на который календарь согласен перейти: только комната `/app/speaking/<id>`.
 *
 * ⚠️ ПУТЬ ПРИХОДИТ С СЕРВЕРА (поле `link` события, для комнаты его подставляет calendar-service). Любой другой
 * внутренний путь — список комнат, чат, раздел, которого в приложении нет, — кнопки не получает: переход по
 * нему привёл бы на «страница не найдена».
 */
export function calendarInternalPath(link: string | undefined | null): string | null {
  const s = (link ?? '').trim();
  return /^\/app\/speaking\/[A-Za-z0-9-]+$/.test(s) ? s : null;
}
